import { z } from "zod";

import {
  formatChargeDifferentialPercent,
  MAX_DIFFERENTIAL_PERCENT,
  MAX_THRESHOLD_HOURS,
  MIN_DIFFERENTIAL_PERCENT,
  MIN_THRESHOLD_HOURS,
} from "../constants";
import { type Differential } from "../types";

export type ExistingDifferential = Pick<Differential, "thresholdInHours" | "incrementalMultiplier">;

export function makeAddDifferentialFormSchema(existingDifferentials: ExistingDifferential[]) {
  return z
    .object({
      thresholdInHours: z.coerce
        .number({ invalid_type_error: "Threshold is required" })
        .int("Threshold must be a whole number")
        .min(MIN_THRESHOLD_HOURS, `Threshold must be at least ${MIN_THRESHOLD_HOURS}`)
        .max(MAX_THRESHOLD_HOURS, `Threshold must be at most ${MAX_THRESHOLD_HOURS}`),
      differentialPercent: z.coerce
        .number({ invalid_type_error: "Charge differential is required" })
        .int("Charge differential must be a whole percent")
        .min(
          MIN_DIFFERENTIAL_PERCENT,
          `Charge differential must be at least ${MIN_DIFFERENTIAL_PERCENT}%`
        )
        .max(
          MAX_DIFFERENTIAL_PERCENT,
          `Charge differential must be at most ${MAX_DIFFERENTIAL_PERCENT}%`
        ),
    })
    .superRefine((values, context) => {
      const newDifferential = values.differentialPercent / 100;
      for (const existing of existingDifferentials) {
        if (existing.thresholdInHours === values.thresholdInHours) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["thresholdInHours"],
            message: "A tier with this threshold already exists",
          });
        } else if (
          existing.thresholdInHours < values.thresholdInHours &&
          existing.incrementalMultiplier <= newDifferential
        ) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["differentialPercent"],
            message: `Shorter lead times need a higher differential — the ${existing.thresholdInHours}h tier is ${formatChargeDifferentialPercent(existing.incrementalMultiplier)}, so this tier must be lower.`,
          });
        } else if (
          existing.thresholdInHours > values.thresholdInHours &&
          existing.incrementalMultiplier >= newDifferential
        ) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["differentialPercent"],
            message: `Shorter lead times need a higher differential — the ${existing.thresholdInHours}h tier is ${formatChargeDifferentialPercent(existing.incrementalMultiplier)}, so this tier must be higher.`,
          });
        }
      }
    });
}

export type AddDifferentialFormSchema = ReturnType<typeof makeAddDifferentialFormSchema>;
export type AddDifferentialFormInput = z.input<AddDifferentialFormSchema>;
export type AddDifferentialFormOutput = z.output<AddDifferentialFormSchema>;

const PRIMARY_DEFAULT_VALUES: AddDifferentialFormInput = {
  thresholdInHours: 12,
  differentialPercent: 5,
};

const FALLBACK_DEFAULT_VALUES: AddDifferentialFormInput = {
  thresholdInHours: 6,
  differentialPercent: 10,
};

export function getDefaultFormValues(
  existingDifferentials: ExistingDifferential[]
): AddDifferentialFormInput {
  const existingThresholds = new Set(
    existingDifferentials.map((differential) => differential.thresholdInHours)
  );
  if (!existingThresholds.has(PRIMARY_DEFAULT_VALUES.thresholdInHours)) {
    return PRIMARY_DEFAULT_VALUES;
  }
  if (!existingThresholds.has(FALLBACK_DEFAULT_VALUES.thresholdInHours)) {
    return FALLBACK_DEFAULT_VALUES;
  }
  return PRIMARY_DEFAULT_VALUES;
}
